import api from "@/lib/api";

const SINGLE_SHOT = 8 * 1024 * 1024; // <= 8MB: one simple multipart request
const MAX_RETRIES = 5;

// Controller for pause / resume / cancel of a large upload.
export function createUploadController() {
  const ac = new AbortController();
  let paused = false;
  let resumeResolve = null;
  return {
    signal: ac.signal,
    get paused() { return paused; },
    pause() { paused = true; },
    resume() { paused = false; if (resumeResolve) { resumeResolve(); resumeResolve = null; } },
    waitResume() { return new Promise((r) => { resumeResolve = r; }); },
    cancel() { ac.abort(); if (resumeResolve) resumeResolve(); },
  };
}

// Uploads a file. Small files go in one request; large files use resumable
// chunked upload with auto-retry + pause/resume. onProgress gets 0..1.
export async function uploadFile(file, { onProgress, controller } = {}) {
  if (file.size <= SINGLE_SHOT) {
    const fd = new FormData();
    fd.append("file", file, file.name);
    const { data } = await api.post("/files/upload", fd, {
      signal: controller?.signal,
      onUploadProgress: (e) => e.total && onProgress?.(e.loaded / e.total),
    });
    return data;
  }

  const init = await api.post("/files/upload/init", {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    size: file.size,
  });
  const uploadId = init.data.upload_id;
  const chunkSize = init.data.chunk_size || SINGLE_SHOT;

  let offset = 0;
  while (offset < file.size) {
    if (controller?.paused) await controller.waitResume();

    const end = Math.min(offset + chunkSize, file.size);
    const chunk = file.slice(offset, end);
    const contentRange = `bytes ${offset}-${end - 1}/${file.size}`;

    let attempt = 0;
    while (true) {
      try {
        const { data } = await api.put(`/files/upload/${uploadId}/chunk`, chunk, {
          headers: { "Content-Range": contentRange, "Content-Type": "application/octet-stream" },
          signal: controller?.signal,
        });
        if (data.status === "complete") { onProgress?.(1); return data.file; }
        offset = data.received;
        onProgress?.(offset / file.size);
        break;
      } catch (err) {
        if (controller?.signal?.aborted) throw err;
        if (++attempt > MAX_RETRIES) throw err;
        await new Promise((r) => setTimeout(r, Math.min(1000 * attempt, 5000)));
        // ask the server how many bytes it actually has, then resume from there
        try {
          const { data } = await api.put(`/files/upload/${uploadId}/chunk`, null, {
            headers: { "Content-Range": `bytes */${file.size}` },
            signal: controller?.signal,
          });
          if (data.status === "complete") { onProgress?.(1); return data.file; }
          offset = data.received;
        } catch { /* keep retrying the same chunk */ }
      }
    }
  }
  throw new Error("Upload did not complete");
}
